import React, { useEffect, useState } from 'react';

export type NodeStatus = 'pending' | 'loading' | 'completed' | 'failed';

export interface LoadingStates {
  gateway: NodeStatus;
  system: NodeStatus;
  preferences: NodeStatus;
  templates: NodeStatus;
  matter: NodeStatus;
  clioSync: NodeStatus;
  tasks: NodeStatus;
  proofGraph: NodeStatus;
  directory: NodeStatus;
  contacts: NodeStatus;
}

interface DbLoadingGraphProps {
  states: LoadingStates;
}

export const DbLoadingGraph: React.FC<DbLoadingGraphProps> = ({ states }) => {
  const [time, setTime] = useState(0);

  // Simple animation tick for the swaying motion
  useEffect(() => {
    let animationFrame: number;
    const tick = (): void => {
      setTime(prev => prev + 1);
      animationFrame = requestAnimationFrame(tick);
    };
    animationFrame = requestAnimationFrame(tick);
    return (): void => cancelAnimationFrame(animationFrame);
  }, []);

  // Calculate progress
  const nodeKeys: (keyof LoadingStates)[] = [
    'gateway',
    'preferences',
    'templates',
    'system',
    'contacts',
    'directory',
    'clioSync',
    'tasks',
    'proofGraph',
    'matter',
  ];
  const completedCount = nodeKeys.filter(k => states[k] === 'completed').length;
  const progressPercent = Math.round((completedCount / nodeKeys.length) * 100);

  // Radial progress calculations
  const radialRadius = 54;
  const strokeWidth = 8;
  const circumference = 2 * Math.PI * radialRadius;
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

  // Swaying scale physics
  // Tilt angles sways back and forth depending on progress, settling at 0% at 100% progress
  const baseTilt = -14 * (1 - progressPercent / 100);
  const swayOffset = Math.sin(time * 0.05) * 3 * (1 - progressPercent / 100);
  const tilt = baseTilt + swayOffset;

  // Scale geometry coordinates (SVG size 400x240)
  const pivotX = 200;
  const pivotY = 50;
  const beamHalfLength = 100;

  // Convert tilt angle to radians for coordinates calculation
  const rad = (tilt * Math.PI) / 180;
  const beamLeftX = pivotX - beamHalfLength * Math.cos(rad);
  const beamLeftY = pivotY - beamHalfLength * Math.sin(rad);
  const beamRightX = pivotX + beamHalfLength * Math.cos(rad);
  const beamRightY = pivotY + beamHalfLength * Math.sin(rad);

  // Pans hang straight down
  const panLeftX = beamLeftX;
  const panLeftY = beamLeftY + 90;
  const panRightX = beamRightX;
  const panRightY = beamRightY + 90;

  // Weight nodes mapping
  const getWeightCoords = (id: string, status: NodeStatus): { x: number; y: number; } => {
    let xOffset = 0;
    let isLeft = true;

    // Distribute nodes horizontally on the pans
    if (id === 'preferences') { xOffset = -14; isLeft = true; }
    else if (id === 'templates') { xOffset = 0; isLeft = true; }
    else if (id === 'contacts') { xOffset = 14; isLeft = true; }
    else if (id === 'clioSync') { xOffset = -21; isLeft = false; }
    else if (id === 'tasks') { xOffset = -7; isLeft = false; }
    else if (id === 'proofGraph') { xOffset = 7; isLeft = false; }
    else if (id === 'gateway') { xOffset = 21; isLeft = false; } // Put gateway as a baseline weight on the right

    const panX = isLeft ? panLeftX : panRightX;
    const panY = isLeft ? panLeftY : panRightY;

    // Hover heights based on status
    let yOffset = -8; // completed sits on the pan
    if (status === 'pending') {
      yOffset = -55; // pending floats high
    } else if (status === 'loading') {
      yOffset = -35; // loading floats midway
    }

    return { x: panX + xOffset, y: panY + yOffset };
  };

  const renderWeight = (id: string, label: string, status: NodeStatus): React.JSX.Element => {
    const coords = getWeightCoords(id, status);
    let fill = '#e2e8f0';
    let stroke = '#cbd5e1';
    let textFill = '#64748b';
    let glowClass = '';

    if (status === 'completed') {
      fill = 'var(--t4s)';
      stroke = 'var(--t4)';
      textFill = 'var(--t4)';
    } else if (status === 'loading') {
      fill = 'var(--primary-soft)';
      stroke = 'var(--primary)';
      textFill = 'var(--primary)';
      glowClass = 'pulse-glow';
    } else if (status === 'failed') {
      fill = 'var(--t0s)';
      stroke = 'var(--t0)';
      textFill = 'var(--t0)';
    }

    return (
      <g key={id}>
        <circle
          cx={coords.x}
          cy={coords.y}
          r="8.5"
          fill={fill}
          stroke={stroke}
          strokeWidth="1.5"
          className={`scale-weight ${glowClass}`}
          style={{
            transition: 'cx 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94), cy 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
          }}
        />
        <text
          x={coords.x}
          y={coords.y + 3}
          textAnchor="middle"
          fontSize="7.5px"
          fontWeight="bold"
          fill={textFill}
          style={{
            transition: 'x 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94), y 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275), fill 0.3s ease',
            pointerEvents: 'none'
          }}
        >
          {label}
        </text>
      </g>
    );
  };

  const getStatusIcon = (status: NodeStatus): React.JSX.Element => {
    if (status === 'completed') return <span style={{ color: 'var(--t4)', marginRight: '6px' }}>✓</span>;
    if (status === 'loading') return <span className="mini-spinner" style={{ marginRight: '6px' }}></span>;
    if (status === 'failed') return <span style={{ color: 'var(--t0)', marginRight: '6px' }}>✗</span>;
    return <span style={{ color: 'var(--muted)', opacity: 0.4, marginRight: '6px' }}>○</span>;
  };

  return (
    <div className="db-loading-overlay">
      <style>{`
        .db-loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(244, 246, 250, 0.65);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          display: grid;
          place-items: center;
          z-index: 99999;
          font-family: var(--g);
          animation: fadeInOverlay 0.35s ease-out;
        }

        @keyframes fadeInOverlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .db-loading-card {
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(228, 233, 241, 0.8);
          border-radius: 16px;
          padding: 32px;
          width: 720px;
          max-width: 95%;
          box-shadow: 0 20px 40px rgba(27, 42, 74, 0.08);
          display: flex;
          gap: 32px;
          align-items: center;
          position: relative;
          border: 1px solid rgba(255, 255, 255, 0.6);
        }

        .db-loading-left {
          flex: 0 0 200px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .db-loading-right {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .pulse-glow {
          animation: pulseShadow 1.5s infinite ease-in-out;
        }

        @keyframes pulseShadow {
          0%, 100% { filter: drop-shadow(0 0 1px rgba(37, 87, 214, 0.3)); }
          50% { filter: drop-shadow(0 0 6px rgba(37, 87, 214, 0.7)); }
        }

        .scale-weight {
          transition: fill 0.3s ease, stroke 0.3s ease;
        }

        .mini-spinner {
          display: inline-block;
          width: 10px;
          height: 10px;
          border: 2px solid var(--line);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .list-section {
          background: rgba(244, 246, 250, 0.5);
          border-radius: 10px;
          padding: 12px 16px;
          border: 1px solid var(--line-soft);
        }

        .list-title {
          font-size: 12px;
          font-weight: 700;
          color: var(--navy);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 8px;
        }

        .list-item {
          display: flex;
          align-items: center;
          font-size: 12px;
          color: var(--navy);
          margin-bottom: 4px;
          font-weight: 500;
        }

        .list-item.completed {
          opacity: 0.85;
        }
        .list-item.pending {
          opacity: 0.5;
        }
      `}</style>

      <div className="db-loading-card">
        {/* Left column: Circular Progress */}
        <div className="db-loading-left">
          <div style={{ position: 'relative', width: '130px', height: '130px', marginBottom: '16px' }}>
            <svg width="130" height="130" viewBox="0 0 130 130" style={{ transform: 'rotate(-90deg)' }}>
              {/* Background Track */}
              <circle
                cx="65"
                cy="65"
                r={radialRadius}
                fill="transparent"
                stroke="var(--line-soft)"
                strokeWidth={strokeWidth}
              />
              {/* Progress Circle */}
              <circle
                cx="65"
                cy="65"
                r={radialRadius}
                fill="transparent"
                stroke="var(--primary)"
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.4s ease' }}
              />
            </svg>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '24px', fontWeight: 700, color: 'var(--navy)' }}>{progressPercent}%</span>
              <span style={{ fontSize: '12px', color: 'var(--muted)', fontWeight: 600 }}>BALANCING</span>
            </div>
          </div>

          <h3 style={{ fontSize: '15px', color: 'var(--navy)', fontWeight: 600, marginBottom: '4px' }}>
            Synchronizing Case
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.4', padding: '0 8px' }}>
            Retrieving files and proof models from database...
          </p>
        </div>

        {/* Right column: The Scales of Justice Graphic */}
        <div className="db-loading-right">
          <div style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: '12px', height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <svg width="380" height="220" viewBox="0 0 400 240">
              {/* 1. Base / Pillar of Scale */}
              {/* Base */}
              <path d="M 160 215 L 240 215 M 175 215 L 180 205 L 220 205 L 225 215 Z" fill="var(--muted2)" stroke="var(--line)" strokeWidth="1" />
              {/* Main Column */}
              <rect x="196" y="55" width="8" height="150" fill="var(--muted2)" rx="2" />
              {/* Central Pivot circle */}
              <circle cx={pivotX} cy={pivotY} r="7" fill="var(--navy)" stroke="var(--line)" strokeWidth="1" />

              {/* 2. Scale Beam (rotates with tilt) */}
              <g style={{ transition: 'transform 0.1s ease' }}>
                {/* The main crossbeam */}
                <line
                  x1={beamLeftX}
                  y1={beamLeftY}
                  x2={beamRightX}
                  y2={beamRightY}
                  stroke="var(--navy)"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
                
                {/* Hooks on ends */}
                <circle cx={beamLeftX} cy={beamLeftY} r="2.5" fill="var(--navy)" />
                <circle cx={beamRightX} cy={beamRightY} r="2.5" fill="var(--navy)" />
              </g>

              {/* 3. Strings hanging from hooks (always hang straight down) */}
              {/* Left strings */}
              <line x1={beamLeftX} y1={beamLeftY} x2={panLeftX - 22} y2={panLeftY} stroke="var(--muted)" strokeWidth="1" />
              <line x1={beamLeftX} y1={beamLeftY} x2={panLeftX + 22} y2={panLeftY} stroke="var(--muted)" strokeWidth="1" />

              {/* Right strings */}
              <line x1={beamRightX} y1={beamRightY} x2={panRightX - 22} y2={panRightY} stroke="var(--muted)" strokeWidth="1" />
              <line x1={beamRightX} y1={beamRightY} x2={panRightX + 22} y2={panRightY} stroke="var(--muted)" strokeWidth="1" />

              {/* 4. The Plates / Pans (always stay horizontal) */}
              {/* Left Pan */}
              <path
                d={`M ${panLeftX - 25} ${panLeftY} L ${panLeftX + 25} ${panLeftY} C ${panLeftX + 20} ${panLeftY + 8} ${panLeftX - 20} ${panLeftY + 8} ${panLeftX - 25} ${panLeftY} Z`}
                fill="var(--muted2)"
                stroke="var(--navy)"
                strokeWidth="1.5"
                style={{ transition: 'd 0.1s ease' }}
              />
              {/* Right Pan */}
              <path
                d={`M ${panRightX - 25} ${panRightY} L ${panRightX + 25} ${panRightY} C ${panRightX + 20} ${panRightY + 8} ${panRightX - 20} ${panRightY + 8} ${panRightX - 25} ${panRightY} Z`}
                fill="var(--muted2)"
                stroke="var(--navy)"
                strokeWidth="1.5"
                style={{ transition: 'd 0.1s ease' }}
              />

              {/* 5. Render Weights (Hover or on pans) */}
              {/* Left Pan Weights (System Context) */}
              {renderWeight('preferences', 'P', states.preferences)}
              {renderWeight('templates', 'T', states.templates)}
              {renderWeight('contacts', 'C', states.contacts)}

              {/* Right Pan Weights (Matter Context) */}
              {renderWeight('clioSync', 'M', states.clioSync)}
              {renderWeight('tasks', 'K', states.tasks)}
              {renderWeight('proofGraph', 'G', states.proofGraph)}
              {renderWeight('gateway', 'L', states.gateway)}
            </svg>
          </div>

          {/* Quick list checklist for visibility */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="list-section">
              <div className="list-title">System & Directory</div>
              <div className={`list-item ${states.preferences === 'completed' ? 'completed' : states.preferences}`}>
                {getStatusIcon(states.preferences)}
                Preferences
              </div>
              <div className={`list-item ${states.templates === 'completed' ? 'completed' : states.templates}`}>
                {getStatusIcon(states.templates)}
                Templates Registry
              </div>
              <div className={`list-item ${states.contacts === 'completed' ? 'completed' : states.contacts}`}>
                {getStatusIcon(states.contacts)}
                Contacts Database
              </div>
            </div>

            <div className="list-section">
              <div className="list-title">Matter Sync</div>
              <div className={`list-item ${states.clioSync === 'completed' ? 'completed' : states.clioSync}`}>
                {getStatusIcon(states.clioSync)}
                Matter Metadata
              </div>
              <div className={`list-item ${states.tasks === 'completed' ? 'completed' : states.tasks}`}>
                {getStatusIcon(states.tasks)}
                Active Clio Tasks
              </div>
              <div className={`list-item ${states.proofGraph === 'completed' ? 'completed' : states.proofGraph}`}>
                {getStatusIcon(states.proofGraph)}
                Lattice Proof Graph
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
